import { faBuilding } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft, faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmailCampaign } from '@spike/email-marketing-model';
import { Chip } from 'components/UI/V3/Chip/Chip';
import { FunctionComponent } from 'react';

import styles from './DetailHeader.module.scss';

interface DetailHeaderProps {
    campaign: EmailCampaign;
    /**
     * Send timestamp — mirrored from `stats.sentAt` for defensive fallback if
     * the nested campaign object hasn't got its own `sentAt` populated yet.
     * Not currently rendered here (the info panel below shows it) but kept in
     * the props so the container has a single place to pass the effective
     * value.
     */
    sentAt: string | null;
    onBack: () => void;
    /** Click on the "View Email" link → opens the ViewEmailModal. */
    onViewEmail: () => void;
}

/**
 * Top strip of the Campaign Detail view: back arrow + campaign name + a
 * secondary "View Email" link that opens the full-window email preview modal.
 * Intentionally minimal — the info panel below carries all the metadata
 * (recipients, subject, status), so this row stays focused on identity +
 * navigation.
 *
 * The Export button from the extended Figma design is intentionally omitted
 * for MVP (no export endpoint on the backend yet); when product ships it, a
 * secondary CTA slot on the right side is the natural place to drop it in.
 */
const DetailHeader: FunctionComponent<DetailHeaderProps> = ({ campaign, onBack, onViewEmail }) => {
    return (
        <header className={styles.root}>
            <button
                type="button"
                className={styles.backButton}
                onClick={onBack}
                aria-label="Back to campaigns"
            >
                <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <div className={styles.titleBlock}>
                <h1 className={styles.title}>{campaign.name}</h1>
                {campaign.sentByOrganization && (
                    <div className={styles.origin}>
                        <Chip
                            icon={faBuilding}
                            label={campaign.originLabel ?? 'Sent by organization'}
                        />
                    </div>
                )}
                <button
                    type="button"
                    className={styles.viewEmailLink}
                    onClick={onViewEmail}
                >
                    View Email
                    <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        className={styles.viewEmailIcon}
                    />
                </button>
            </div>
        </header>
    );
};

export default DetailHeader;
